import { css, keyframes } from '@emotion/css';
import {
  appColors,
  breakpoints,
  fontFamily,
  shadows,
} from '../../styles/variables.style';

const fadeInLeft = keyframes`
  from { opacity: 0; transform: translateX(-50px); }
  to { opacity: 1; transform: translateX(0); }
`;

const fadeInRight = keyframes`
  from { opacity: 0; transform: translateX(50px); }
  to { opacity: 1; transform: translateX(0); }
`;

export const pageWrapper = css`
  background-color: ${appColors.lightGray};
  min-height: 100vh;
  padding-bottom: 100px;
  overflow-x: hidden;
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const slowRotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const heroContainer = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 20px 60px;
  max-width: 1300px;
  margin: 0 auto;
  gap: 40px;

  @media (min-width: ${breakpoints.lg.up}) {
    flex-direction: row;
    justify-content: space-between;
    padding: 40px 20px 80px;
    gap: 80px;
  }
`;

export const heroLeft = css`
  flex: 1.2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  animation: ${fadeInLeft} 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
  width: 100%;

  @media (max-width: ${breakpoints.lg.down}) {
    align-items: center;
    text-align: center;
  }
`;

export const welcomeText = css`
  font-family: ${fontFamily.poppins};
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 2px;
  color: ${appColors.gray800};
  margin-bottom: 20px;
  font-weight: 700;
`;

export const headline = css`
  font-family: ${fontFamily.poppins};
  font-size: 3rem;
  font-weight: 700;
  margin: 0 0 1.5rem;
  line-height: 1.2;
  color: ${appColors.gray800};

  span {
    color: ${appColors.orange};
  }

  @media (min-width: ${breakpoints.lg.up}) {
    font-size: 4.2rem;
    margin: 0 0 2rem;
  }
`;

export const line = css`
  font-family: ${fontFamily.poppins};
  font-size: 1rem;
  color: ${appColors.gray800};
  margin: 0 0 2.5rem;
  line-height: 1.8;
  max-width: 600px;
  opacity: 0.8;

  @media (min-width: ${breakpoints.lg.up}) {
    font-size: 1.1rem;
  }
`;

export const heroRight = css`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  animation: ${fadeInRight} 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) forwards;
`;

export const decorationCard1 = css`
  position: absolute;
  width: 320px;
  height: 380px;
  background: ${appColors.orange};
  border-radius: 40px;
  transform: rotate(-15deg);
  z-index: -1;
  opacity: 0.8;
  box-shadow: ${shadows.neumorphic};

  @media (max-width: ${breakpoints.md.down}) {
    width: 220px;
    height: 280px;
  }
`;

export const decorationCard2 = css`
  position: absolute;
  width: 340px;
  height: 400px;
  background: transparent;
  border: 2px solid ${appColors.orange};
  border-radius: 40px;
  transform: rotate(10deg);
  z-index: -2;
  opacity: 0.3;

  @media (max-width: ${breakpoints.md.down}) {
    width: 240px;
    height: 300px;
  }
`;

export const floatingShape = css`
  position: absolute;
  color: ${appColors.orange};
  font-size: 24px;
  font-weight: 700;
  opacity: 0.6;
  z-index: 1;
  animation: ${float} 4s ease-in-out infinite;

  &.shape-plus {
    top: 10%;
    right: 10%;
    font-size: 40px;
    animation-delay: 0.5s;
  }

  &.shape-triangle {
    bottom: 10%;
    left: 10%;
    font-size: 30px;
    animation-delay: 1.2s;
  }

  &.shape-circle {
    top: 50%;
    right: -20px;
    font-size: 45px;
    animation: ${float} 5s ease-in-out infinite, ${slowRotate} 15s linear infinite;
  }

  &.shape-zigzag {
    top: 20%;
    left: -40px;
    animation-delay: 2s;
    svg {
      stroke: ${appColors.orange};
    }
  }
`;

export const heroPersonImg = css`
  width: 100%;
  max-width: 850px;
  height: auto;
  position: relative;
  z-index: 2;
  filter: drop-shadow(20px 30px 50px rgba(0,0,0,0.15));

  @media (max-width: ${breakpoints.lg.down}) {
    max-width: 350px;
  }
`;

export const socialSection = css`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  margin-top: 40px;

  @media (max-width: ${breakpoints.lg.down}) {
    justify-content: center;
  }
`;

export const socialGroup = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const socialTitle = css`
  font-family: ${fontFamily.poppins};
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${appColors.gray800};
  font-weight: 500;
`;

export const iconList = css`
  display: flex;
  gap: 20px;
`;

export const iconButton = css`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${appColors.lightGray};
  border-radius: 6px;
  box-shadow: ${shadows.neumorphic};
  color: ${appColors.gray800};
  font-size: 20px;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    transform: translateY(-5px);
    color: ${appColors.orange};
    box-shadow: ${shadows.neumorphicHover};
    text-decoration: none;
  }

  &.orange-icon {
    color: ${appColors.orange};
  }

  img {
    width: 24px;
    height: 24px;
    object-fit: contain;
  }
`;

export const premiumButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 16px 40px;
  font-size: 1rem;
  font-weight: 600;
  color: ${appColors.orange};
  background: ${appColors.lightGray};
  border-radius: 6px;
  text-decoration: none;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: ${shadows.neumorphic};
  transition: all 0.3s ease;

  @media (max-width: ${breakpoints.lg.down}) {
    margin-left: 0px;
  }

  &:hover {
    transform: translateY(-3px);
    box-shadow: ${shadows.neumorphicHover};
    color: ${appColors.orange};
    text-decoration: none;
  }
`;
